import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Users, MessageSquare, Shield, Bell, LogOut, Search, CheckCircle2, AlertCircle, Clock, Send, Plus, Trash2, Lock, Mail, User, Activity, CreditCard, Menu, X, ArrowLeft, Power, Ban, Package, Tag, UserCheck, Bot, Settings, Eye, Paperclip } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { useLanguage } from '../hooks/useLanguage';
import { dbUsers, dbProducts, dbOffers, dbCustomers, dbSupportTickets, dbTicketReplies, dbAdminStaff, dbUserNotifications, SupabaseUser, SupabaseTicket, SupabaseTicketReply, SupabaseAdminStaff, dbActivityLog, ActivityLogEntry, logActivity } from '../utils/supabase';
import { hashPassword, verifyPassword } from '../utils/security';
import { compressImageToBase64 } from '../utils/api';
import { dbActivityLog, ActivityLogEntry, logActivity } from '../utils/activityLog';
// --- Types ---
type AdminSection = 'overview' | 'users' | 'tickets' | 'staff' | 'notifications';
interface AdminSession {
    email: string;
    name: string;
    role: 'super_admin' | 'admin' | 'support';
    permissions: string[];
    loginAt: number;
}
// --- Hardcoded Super Admin ---
const SUPER_ADMIN = {
    email: 'wael@operines.ai',
    passwordHash: '7b4c4f3e6a2d1b8c9f0e5a7d3b2c1f9e8d4a6b5c7f0e2d1a3b4c5f6e7d8a9b0c',
    name: 'Wael Eldeniney',
    role: 'super_admin' as const,
    permissions: ['all'],
};
export function AdminDashboardPage() {
    const { language } = useLanguage();
    const { showToast } = useToast();
    const isRTL = language === 'ar';
    // --- Auth State ---
    const [adminUser, setAdminUser] = useState<AdminSession | null>(null);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    // --- Dashboard State ---
    const [activeSection, setActiveSection] = useState<AdminSection>('overview');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    // --- Data State ---
    const [users, setUsers] = useState<SupabaseUser[]>([]);
    const [tickets, setTickets] = useState<SupabaseTicket[]>([]);
    const [staff, setStaff] = useState<SupabaseAdminStaff[]>([]);
    // --- Search & Filter State ---
    const [userSearch, setUserSearch] = useState('');
    const [ticketStatusFilter, setTicketStatusFilter] = useState<string>('all');
    // --- User Detail State ---
    const [selectedUser, setSelectedUser] = useState<SupabaseUser | null>(null);
    const [userProducts, setUserProducts] = useState<any[]>([]);
    const [userOffers, setUserOffers] = useState<any[]>([]);
    const [userCustomers, setUserCustomers] = useState<any[]>([]);
    const [userTickets, setUserTickets] = useState<SupabaseTicket[]>([]);
    const [userActivities, setUserActivities] = useState<ActivityLogEntry[]>([]);
    const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);
    const [isTogglingUserBot, setIsTogglingUserBot] = useState(false);
    const [isTogglingUserSuspend, setIsTogglingUserSuspend] = useState(false);
    // --- Notification State ---
    const [notifEmail, setNotifEmail] = useState('');
    const [notifTitleEn, setNotifTitleEn] = useState('');
    const [notifTitleAr, setNotifTitleAr] = useState('');
    const [notifMessageEn, setNotifMessageEn] = useState('');
    const [notifMessageAr, setNotifMessageAr] = useState('');
    const [notifType, setNotifType] = useState<'info' | 'warning' | 'success'>('info');
    const [isSendingNotif, setIsSendingNotif] = useState(false);
    // --- Staff State ---
    const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
    const [staffName, setStaffName] = useState('');
    const [staffEmail, setStaffEmail] = useState('');
    const [staffPassword, setStaffPassword] = useState('');
    const [staffRole, setStaffRole] = useState<'admin' | 'support'>('support');
    const [staffPermissions, setStaffPermissions] = useState<string[]>([]);
    const [isAddingStaff, setIsAddingStaff] = useState(false);
    // --- Ticket Detail State ---
    const [selectedTicket, setSelectedTicket] = useState<SupabaseTicket | null>(null);
    const [ticketReplies, setTicketReplies] = useState<SupabaseTicketReply[]>([]);
    const [replyMessage, setReplyMessage] = useState('');
    const [isReplying, setIsReplying] = useState(false);
    const [attachmentBase64, setAttachmentBase64] = useState<string | null>(null);
    // --- Admin Create Ticket State ---
    const [isCreateTicketOpen, setIsCreateTicketOpen] = useState(false);
    const [createTicketSubject, setCreateTicketSubject] = useState('');
    const [createTicketMessage, setCreateTicketMessage] = useState('');
    const [createTicketPriority, setCreateTicketPriority] = useState<'low' | 'medium' | 'high'>('medium');
    const [isCreatingTicket, setIsCreatingTicket] = useState(false);
    // --- Check Auth on Mount ---
    useEffect(() => {
        const session = localStorage.getItem('operines_admin_session');
        if (session) {
            try {
                const parsed = JSON.parse(session);
                // Simple expiry check (24 hours)
                if (Date.now() - parsed.loginAt < 24 * 60 * 60 * 1000) {
                    setAdminUser(parsed);
                }
                else {
                    localStorage.removeItem('operines_admin_session');
                }
            }
            catch {
                localStorage.removeItem('operines_admin_session');
            }
        }
        setIsCheckingAuth(false);
    }, []);
    // --- Load Data when Auth'd ---
    useEffect(() => {
        if (adminUser) {
            loadDashboardData();
        }
    }, [adminUser]);
    const loadDashboardData = async () => {
        setIsLoadingData(true);
        try {
            const [usersData, ticketsData, staffData] = await Promise.all([
                dbUsers.getAll(),
                dbSupportTickets.getAllTickets(),
                dbAdminStaff.getAll(),
            ]);
            setUsers(usersData);
            setTickets(ticketsData);
            setStaff(staffData);
        }
        catch (error) {
            console.error('Failed to load admin data:', error);
            showToast('error', isRTL ? 'فشل تحميل البيانات' : 'Failed to load data');
        }
        finally {
            setIsLoadingData(false);
        }
    };
    // --- Handlers ---
    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!loginEmail || !loginPassword)
            return;
        setIsLoggingIn(true);
        try {
            let session: AdminSession | null = null;
            // Check if super admin password matches hash OR the base64 encoded fallback
            const isSuperAdminPassword = (await verifyPassword(loginPassword, SUPER_ADMIN.passwordHash)) ||
                (loginPassword === atob('V2FlbEAyODA1'));
            // 1. Check hardcoded super admin
            if (loginEmail.toLowerCase() === SUPER_ADMIN.email.toLowerCase() &&
                isSuperAdminPassword) {
                session = {
                    email: SUPER_ADMIN.email,
                    name: SUPER_ADMIN.name,
                    role: SUPER_ADMIN.role,
                    permissions: SUPER_ADMIN.permissions,
                    loginAt: Date.now(),
                };
                // Log admin login
                logActivity.adminAction(SUPER_ADMIN.email, 'Super Admin Login', SUPER_ADMIN.name).catch(console.error);
            }
            else {
                // 2. Check Supabase staff table
                const staffUser = await dbAdminStaff.getByEmail(loginEmail);
                if (staffUser && staffUser.is_active) {
                    const isValid = await verifyPassword(loginPassword, staffUser.password);
                    if (isValid) {
                        session = {
                            email: staffUser.email,
                            name: staffUser.name,
                            role: staffUser.role as any,
                            permissions: staffUser.permissions || [],
                            loginAt: Date.now(),
                        };
                        // Log staff login
                        logActivity.adminAction(staffUser.email, 'Staff Login', staffUser.name).catch(console.error);
                    }
                }
            }
            if (session) {
                setAdminUser(session);
                localStorage.setItem('operines_admin_session', JSON.stringify(session));
                showToast('success', isRTL ? 'تم تسجيل الدخول' : 'Login successful');
            }
            else {
                showToast('error', isRTL ? 'بيانات الدخول غير صحيحة' : 'Invalid credentials');
            }
        }
        catch (error) {
            console.error('Login error:', error);
            showToast('error', isRTL ? 'حدث خطأ' : 'An error occurred');
        }
        finally {
            setIsLoggingIn(false);
        }
    };
    const handleLogout = () => {
        setAdminUser(null);
        localStorage.removeItem('operines_admin_session');
    };
    const handleTicketClick = async (ticket: SupabaseTicket) => {
        setSelectedTicket(ticket);
        try {
            const replies = await dbTicketReplies.getByTicket(ticket.id!);
            setTicketReplies(replies);
        }
        catch (error) {
            console.error('Failed to load replies:', error);
        }
    };
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        try {
            const base64 = await compressImageToBase64(file);
            setAttachmentBase64(base64);
        }
        catch (error) {
            console.error('Failed to compress image:', error);
            showToast('error', isRTL ? 'فشل تحميل الصورة' : 'Failed to load image');
        }
    };
    const renderMessageContent = (msg: string) => {
        const parts = msg.split('[ATTACHMENT:');
        if (parts.length === 1)
            return <p className="whitespace-pre-wrap">{msg}</p>;
        const textPart = parts[0].trim();
        const attachmentPart = parts[1].split(']')[0];
        return (<div className="space-y-2">
        {textPart && <p className="whitespace-pre-wrap">{textPart}</p>}
        {attachmentPart && (<img src={attachmentPart} alt="Attachment" className="max-w-full h-auto rounded-lg border border-white/20 max-h-64 object-contain"/>)}
      </div>);
    };
    const handleReplyTicket = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedTicket ||
            (!replyMessage.trim() && !attachmentBase64) ||
            !adminUser)
            return;
        setIsReplying(true);
        try {
            let finalMessage = replyMessage;
            if (attachmentBase64) {
                finalMessage += `\n\n[ATTACHMENT:${attachmentBase64}]`;
            }
            const reply = await dbTicketReplies.create({
                ticket_id: selectedTicket.id!,
                from_type: 'admin',
                from_name: adminUser.name,
                message: finalMessage,
            });
            if (reply) {
                setTicketReplies([...ticketReplies, reply]);
                setReplyMessage('');
                setAttachmentBase64(null);
                // Notify user
                await dbUserNotifications.create({
                    user_email: selectedTicket.user_email,
                    title: 'New reply on your ticket',
                    title_ar: 'رد جديد على تذكرتك',
                    message: `Admin has replied to your ticket: ${selectedTicket.subject}`,
                    message_ar: `قام فريق الدعم بالرد على تذكرتك: ${selectedTicket.subject}`,
                    type: 'info',
                    read: false,
                    sent_by: 'System',
                });
                // Update ticket status to in_progress if it was open
                if (selectedTicket.status === 'open') {
                    await dbSupportTickets.updateStatus(selectedTicket.id!, 'in_progress');
                    setSelectedTicket({
                        ...selectedTicket,
                        status: 'in_progress',
                    });
                    setTickets(tickets.map((t) => t.id === selectedTicket.id
                        ? {
                            ...t,
                            status: 'in_progress',
                        }
                        : t));
                }
                showToast('success', isRTL ? 'تم إرسال الرد' : 'Reply sent');
            }
        }
        catch (error) {
            console.error('Reply error:', error);
            showToast('error', isRTL ? 'فشل إرسال الرد' : 'Failed to send reply');
        }
        finally {
            setIsReplying(false);
        }
    };
    const handleUpdateTicketStatus = async (status: SupabaseTicket['status']) => {
        if (!selectedTicket)
            return;
        try {
            const success = await dbSupportTickets.updateStatus(selectedTicket.id!, status);
            if (success) {
                setSelectedTicket({
                    ...selectedTicket,
                    status,
                });
                setTickets(tickets.map((t) => t.id === selectedTicket.id
                    ? {
                        ...t,
                        status,
                    }
                    : t));
                showToast('success', isRTL ? 'تم تحديث الحالة' : 'Status updated');
            }
        }
        catch (error) {
            console.error('Status update error:', error);
        }
    };
    const loadUserDetails = async (user: SupabaseUser) => {
        setSelectedUser(user);
        setIsLoadingUserDetails(true);
        try {
            const [products, offers, customers, tickets, activities] = await Promise.all([
                dbProducts.getAll(user.email),
                dbOffers.getAll(user.email),
                dbCustomers.getAll(user.email, user.instance_name),
                dbSupportTickets.getAll(user.email),
                dbActivityLog.getByUser(user.email),
            ]);
            setUserProducts(products);
            setUserOffers(offers);
            setUserCustomers(customers);
            setUserTickets(tickets);
            setUserActivities(activities);
        }
        catch (error) {
            console.error('Failed to load user details:', error);
            showToast('error', isRTL ? 'فشل تحميل بيانات المستخدم' : 'Failed to load user details');
        }
        finally {
            setIsLoadingUserDetails(false);
        }
    };
    const handleToggleUserSuspend = async (user: SupabaseUser) => {
        const willSuspend = !user.is_account_suspended;
        if (willSuspend &&
            !confirm(isRTL
                ? 'هل أنت متأكد من تعليق هذا الحساب؟'
                : 'Are you sure you want to suspend this account?')) {
            return;
        }
        setIsTogglingUserSuspend(true);
        try {
            const success = await dbUsers.update(user.email, {
                is_account_suspended: willSuspend,
            });
            if (success) {
                const updatedUser = {
                    ...user,
                    is_account_suspended: willSuspend,
                };
                setSelectedUser(updatedUser);
                setUsers(users.map((u) => (u.email === user.email ? updatedUser : u)));
                // Log activity
                if (willSuspend) {
                    await logActivity.accountSuspended(user.email, adminUser?.name || 'Admin');
                }
                else {
                    await logActivity.accountActivated(user.email, adminUser?.name || 'Admin');
                }
                // Refresh activities
                const activities = await dbActivityLog.getByUser(user.email);
                setUserActivities(activities);
                showToast('success', isRTL
                    ? willSuspend
                        ? 'تم تعليق الحساب'
                        : 'تم تفعيل الحساب'
                    : willSuspend
                        ? 'Account suspended'
                        : 'Account activated');
            }
            else {
                showToast('error', isRTL ? 'فشل التحديث' : 'Update failed');
            }
        }
        catch (error) {
            console.error('Toggle suspend error:', error);
            showToast('error', isRTL ? 'حدث خطأ' : 'An error occurred');
        }
        finally {
            setIsTogglingUserSuspend(false);
        }
    };
    const handleToggleUserBot = async (user: SupabaseUser, enable: boolean) => {
        if (!user.instance_name) {
            showToast('error', isRTL ? 'لا يوجد اسم للنسخة' : 'No instance name found');
            return;
        }
        setIsTogglingUserBot(true);
        try {
            const url = enable
                ? 'https://n8n.operines.me/webhook/seup-whatsapp-operines1'
                : 'https://n8n.operines.me/webhook/disable';
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    instance_name: user.instance_name,
                }),
            });
            if (response.ok) {
                // Log activity
                await logActivity.botToggle(user.email, enable, adminUser?.name || 'Admin');
                // Refresh activities
                const activities = await dbActivityLog.getByUser(user.email);
                setUserActivities(activities);
                showToast('success', isRTL
                    ? enable
                        ? 'تم تفعيل البوت'
                        : 'تم تعطيل البوت'
                    : enable
                        ? 'Bot enabled'
                        : 'Bot disabled');
            }
            else {
                throw new Error('Webhook failed');
            }
        }
        catch (error) {
            console.error('Toggle bot error:', error);
            showToast('error', isRTL ? 'فشل تحديث البوت' : 'Failed to update bot');
        }
        finally {
            setIsTogglingUserBot(false);
        }
    };
    const handleChangeUserPlan = async (user: SupabaseUser, newPlan: string) => {
        try {
            const oldPlan = user.plan;
            const success = await dbUsers.update(user.email, {
                plan: newPlan,
            });
            if (success) {
                const updatedUser = {
                    ...user,
                    plan: newPlan,
                };
                setSelectedUser(updatedUser);
                setUsers(users.map((u) => (u.email === user.email ? updatedUser : u)));
                // Log activity
                await logActivity.planChange(user.email, oldPlan, newPlan, adminUser?.name || 'Admin');
                // Refresh activities
                const activities = await dbActivityLog.getByUser(user.email);
                setUserActivities(activities);
                showToast('success', isRTL ? 'تم تحديث الخطة' : 'Plan updated successfully');
            }
            else {
                showToast('error', isRTL ? 'فشل التحديث' : 'Update failed');
            }
        }
        catch (error) {
            console.error('Change plan error:', error);
            showToast('error', isRTL ? 'حدث خطأ' : 'An error occurred');
        }
    };
    const handleAdminCreateTicket = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedUser || !createTicketSubject.trim() || !createTicketMessage.trim() || !adminUser)
            return;
        setIsCreatingTicket(true);
        try {
            const ticket = await dbSupportTickets.create({
                user_email: selectedUser.email,
                subject: `[Admin] ${createTicketSubject}`,
                message: createTicketMessage,
                priority: createTicketPriority,
                status: 'open',
            });
            if (ticket) {
                // Add admin's first reply automatically
                await dbTicketReplies.create({
                    ticket_id: ticket.id!,
                    from_type: 'admin',
                    from_name: adminUser.name,
                    message: createTicketMessage,
                });
                // Notify the user
                await dbUserNotifications.create({
                    user_email: selectedUser.email,
                    title: 'New support ticket from admin',
                    title_ar: 'تذكرة دعم جديدة من الإدارة',
                    message: `Admin has opened a support ticket: ${createTicketSubject}`,
                    message_ar: `قام فريق الدعم بفتح تذكرة: ${createTicketSubject}`,
                    type: 'info',
                    read: false,
                    sent_by: adminUser.name,
                });
                // Log activity
                await logActivity.ticketCreated(selectedUser.email, createTicketSubject, adminUser.name);
                // Refresh activities
                const activities = await dbActivityLog.getByUser(selectedUser.email);
                setUserActivities(activities);
                // Update local state
                setUserTickets([ticket, ...userTickets]);
                setTickets([ticket, ...tickets]);
                // Reset form
                setIsCreateTicketOpen(false);
                setCreateTicketSubject('');
                setCreateTicketMessage('');
                setCreateTicketPriority('medium');
                showToast('success', isRTL ? 'تم إنشاء التذكرة بنجاح' : 'Ticket created successfully');
            }
            else {
                showToast('error', isRTL ? 'فشل إنشاء التذكرة' : 'Failed to create ticket');
            }
        }
        catch (error) {
            console.error('Create ticket error:', error);
            showToast('error', isRTL ? 'حدث خطأ' : 'An error occurred');
        }
        finally {
            setIsCreatingTicket(false);
        }
    };
    const handleSendNotification = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!notifEmail ||
            !notifTitleEn ||
            !notifTitleAr ||
            !notifMessageEn ||
            !notifMessageAr ||
            !adminUser) {
            showToast('error', isRTL ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
            return;
        }
        setIsSendingNotif(true);
        try {
            if (notifEmail === 'all') {
                // Send to all users
                const promises = users.map((u) => dbUserNotifications.create({
                    user_email: u.email,
                    title: notifTitleEn,
                    title_ar: notifTitleAr,
                    message: notifMessageEn,
                    message_ar: notifMessageAr,
                    type: notifType,
                    read: false,
                    sent_by: adminUser.name,
                }));
                await Promise.allSettled(promises);
                showToast('success', isRTL
                    ? `تم إرسال الإشعار لـ ${users.length} مستخدم`
                    : `Sent to ${users.length} users`);
            }
            else {
                // Send to specific user
                const success = await dbUserNotifications.create({
                    user_email: notifEmail,
                    title: notifTitleEn,
                    title_ar: notifTitleAr,
                    message: notifMessageEn,
                    message_ar: notifMessageAr,
                    type: notifType,
                    read: false,
                    sent_by: adminUser.name,
                });
                if (success) {
                    showToast('success', isRTL ? 'تم إرسال الإشعار بنجاح' : 'Notification sent successfully');
                }
                else {
                    throw new Error('Failed to create notification');
                }
            }
            // Reset form
            setNotifEmail('');
            setNotifTitleEn('');
            setNotifTitleAr('');
            setNotifMessageEn('');
            setNotifMessageAr('');
            setNotifType('info');
        }
        catch (error) {
            console.error('Send notification error:', error);
            showToast('error', isRTL ? 'فشل إرسال الإشعار' : 'Failed to send notification');
        }
        finally {
            setIsSendingNotif(false);
        }
    };
    // --- Render Helpers ---
    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'open':
                return (<span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <AlertCircle className="w-3 h-3"/>
            {isRTL ? 'مفتوحة' : 'Open'}
          </span>);
            case 'in_progress':
                return (<span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3"/>
            {isRTL ? 'قيد المعالجة' : 'In Progress'}
          </span>);
            case 'resolved':
            case 'closed':
                return (<span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle2 className="w-3 h-3"/>
            {isRTL ? 'مغلقة' : 'Closed'}
          </span>);
            default:
                return null;
        }
    };
    const getPlanBadge = (plan: string) => {
        switch (plan?.toLowerCase()) {
            case 'trial':
                return (<span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Trial
          </span>);
            case 'starter':
                return (<span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Starter
          </span>);
            case 'business':
                return (<span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            Business
          </span>);
            case 'pro':
                return (<span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
            Pro
          </span>);
            default:
                return (<span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {plan || 'None'}
          </span>);
        }
    };
    // --- Views ---
    if (isCheckingAuth) {
        return (<div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-[#4f1964] border-t-transparent rounded-full"/>
      </div>);
    }
    if (!adminUser) {
        return (<div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <motion.div initial={{
                opacity: 0,
                y: 20,
            }} animate={{
                opacity: 1,
                y: 0,
            }} className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#4f1964] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Shield className="w-8 h-8 text-white"/>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isRTL ? 'لوحة تحكم الإدارة' : 'Admin Dashboard'}
            </h1>
            <p className="text-gray-500 mt-2">
              {isRTL ? 'تسجيل الدخول للموظفين فقط' : 'Staff login only'}
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {isRTL ? 'البريد الإلكتروني' : 'Email Address'}
              </label>
              <div className="relative">
                <Mail className="absolute top-3 left-3 w-5 h-5 text-gray-400"/>
                <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required dir="ltr"/>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {isRTL ? 'كلمة المرور' : 'Password'}
              </label>
              <div className="relative">
                <Lock className="absolute top-3 left-3 w-5 h-5 text-gray-400"/>
                <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required dir="ltr"/>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#4f1964] text-white hover:bg-[#3d1350] py-3" disabled={isLoggingIn}>
              {isLoggingIn ? (<div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto"/>) : isRTL ? ('تسجيل الدخول') : ('Login')}
            </Button>
          </form>
        </motion.div>
      </div>);
    }
    const sidebarItems = [
        {
            id: 'overview',
            label: isRTL ? 'نظرة عامة' : 'Overview',
            icon: LayoutDashboard,
        },
        {
            id: 'users',
            label: isRTL ? 'المستخدمين' : 'Users',
            icon: Users,
        },
        {
            id: 'tickets',
            label: isRTL ? 'تذاكر الدعم' : 'Support Tickets',
            icon: MessageSquare,
        },
        {
            id: 'staff',
            label: isRTL ? 'الموظفين' : 'Staff',
            icon: Shield,
        },
        {
            id: 'notifications',
            label: isRTL ? 'الإشعارات' : 'Notifications',
            icon: Bell,
        },
    ];
    // --- Render Sections ---
    const renderOverview = () => {
        const activeUsers = users.filter((u) => !u.is_account_suspended).length;
        const openTickets = tickets.filter((t) => t.status === 'open').length;
        const trialUsers = users.filter((u) => u.plan === 'trial').length;
        const paidUsers = users.filter((u) => u.plan !== 'trial').length;
        return (<div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {isRTL ? 'نظرة عامة' : 'Overview'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
                {
                    label: isRTL ? 'إجمالي المستخدمين' : 'Total Users',
                    value: users.length,
                    icon: Users,
                    color: 'text-blue-600',
                    bg: 'bg-blue-50',
                },
                {
                    label: isRTL ? 'تذاكر مفتوحة' : 'Open Tickets',
                    value: openTickets,
                    icon: AlertCircle,
                    color: 'text-red-600',
                    bg: 'bg-red-50',
                },
                {
                    label: isRTL ? 'مشتركين (مدفوع)' : 'Paid Subscribers',
                    value: paidUsers,
                    icon: CreditCard,
                    color: 'text-green-600',
                    bg: 'bg-green-50',
                },
                {
                    label: isRTL ? 'فترة تجريبية' : 'Trial Users',
                    value: trialUsers,
                    icon: Clock,
                    color: 'text-yellow-600',
                    bg: 'bg-yellow-50',
                },
            ].map((stat, i) => (<div key={i} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <div className={`p-3 rounded-lg ${stat.bg}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`}/>
                </div>
                <span className="text-2xl font-bold text-gray-900">
                  {stat.value}
                </span>
              </div>
              <h3 className="text-gray-600 font-medium">{stat.label}</h3>
            </div>))}
        </div>
      </div>);
    };
    const renderUsers = () => {
        const filteredUsers = users.filter((u) => u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
            u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
            (u.phone && u.phone.includes(userSearch)));
        // User Detail View
        if (selectedUser) {
            return (<div className="space-y-6 max-w-6xl mx-auto">
          <button onClick={() => {
                    setSelectedUser(null);
                    setUserProducts([]);
                    setUserOffers([]);
                    setUserCustomers([]);
                    setUserTickets([]);
                }} className="text-gray-500 hover:text-[#4f1964] flex items-center gap-2 text-sm font-medium">
            <ArrowLeft className="w-4 h-4"/>
            {isRTL ? 'العودة للقائمة' : 'Back to list'}
          </button>

          {isLoadingUserDetails ? (<div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#4f1964] border-t-transparent rounded-full animate-spin"/>
            </div>) : (<>
              {/* User Info Card */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-16 h-16 rounded-full bg-[#4f1964] flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                      {selectedUser.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        {selectedUser.name}
                      </h3>
                      {selectedUser.brand_name && (<p className="text-sm text-gray-600 mb-2">
                          {selectedUser.brand_name}
                        </p>)}
                      <div className="space-y-1.5 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400"/>
                          <span dir="ltr">{selectedUser.email}</span>
                        </div>
                        {selectedUser.phone && (<div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400"/>
                            <span dir="ltr">{selectedUser.phone}</span>
                          </div>)}
                        {selectedUser.instance_name && (<div className="flex items-center gap-2">
                            <Bot className="w-4 h-4 text-gray-400"/>
                            <span>{selectedUser.instance_name}</span>
                          </div>)}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 md:items-end">
                    <div className="flex flex-wrap gap-2">
                      {getPlanBadge(selectedUser.plan)}
                      {selectedUser.is_account_suspended ? (<span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Suspended
                        </span>) : (<span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>)}
                    </div>
                    <p className="text-xs text-gray-500">
                      {isRTL ? 'انضم في:' : 'Joined:'}{' '}
                      {new Date(selectedUser.created_at!).toLocaleDateString()}
                    </p>
                    {selectedUser.trial_start_date && (<p className="text-xs text-gray-500">
                        {isRTL ? 'الفترة التجريبية:' : 'Trial:'}{' '}
                        {new Date(selectedUser.trial_start_date).toLocaleDateString()}{' '}
                        -{' '}
                        {new Date(selectedUser.trial_end_date!).toLocaleDateString()}
                      </p>)}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5"/>
                  {isRTL ? 'إجراءات الحساب' : 'Account Actions'}
                </h4>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => handleToggleUserSuspend(selectedUser)} disabled={isTogglingUserSuspend} className={`flex items-center gap-2 ${selectedUser.is_account_suspended ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}>
                    {isTogglingUserSuspend ? (<div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/>) : selectedUser.is_account_suspended ? (<Power className="w-4 h-4"/>) : (<Ban className="w-4 h-4"/>)}
                    {selectedUser.is_account_suspended
                        ? isRTL
                            ? 'تفعيل الحساب'
                            : 'Activate Account'
                        : isRTL
                            ? 'تعليق الحساب'
                            : 'Suspend Account'}
                  </Button>

                  {selectedUser.instance_name && (<>
                      <Button onClick={() => handleToggleUserBot(selectedUser, true)} disabled={isTogglingUserBot} className="flex items-center gap-2 bg-[#4f1964] hover:bg-[#3d1350] text-white">
                        {isTogglingUserBot ? (<div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/>) : (<Power className="w-4 h-4"/>)}
                        {isRTL ? 'تفعيل البوت' : 'Enable Bot'}
                      </Button>
                      <Button onClick={() => handleToggleUserBot(selectedUser, false)} disabled={isTogglingUserBot} variant="outline" className="flex items-center gap-2">
                        {isTogglingUserBot ? (<div className="w-4 h-4 border-2 border-gray-400/30 border-t-gray-400 rounded-full animate-spin"/>) : (<Ban className="w-4 h-4"/>)}
                        {isRTL ? 'تعطيل البوت' : 'Disable Bot'}
                      </Button>
                    </>)}

                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">
                      {isRTL ? 'تغيير الخطة:' : 'Change Plan:'}
                    </label>
                    <select value={selectedUser.plan} onChange={(e) => handleChangeUserPlan(selectedUser, e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#4f1964] outline-none">
                      <option value="trial">Trial</option>
                      <option value="starter">Starter</option>
                      <option value="business">Business</option>
                      <option value="pro">Pro</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Data Sections Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Products Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-[#4f1964]"/>
                    {isRTL ? 'المنتجات' : 'Products'}
                    <span className="ml-auto text-sm font-normal text-gray-500">
                      ({userProducts.length})
                    </span>
                  </h4>
                  {userProducts.length === 0 ? (<p className="text-sm text-gray-500 text-center py-8">
                      {isRTL ? 'لا توجد منتجات' : 'No products'}
                    </p>) : (<div className="space-y-3 max-h-64 overflow-y-auto">
                      {userProducts.map((p) => (<div key={p.id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex justify-between items-start mb-1">
                            <h5 className="font-medium text-gray-900 text-sm">
                              {p.name}
                            </h5>
                            <span className="text-sm font-bold text-[#4f1964]">
                              {p.price}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mb-1">{p.type}</p>
                          {p.description && (<p className="text-xs text-gray-600 line-clamp-2">
                              {p.description}
                            </p>)}
                        </div>))}
                    </div>)}
                </div>

                {/* Offers Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-[#4f1964]"/>
                    {isRTL ? 'العروض' : 'Offers'}
                    <span className="ml-auto text-sm font-normal text-gray-500">
                      ({userOffers.length})
                    </span>
                  </h4>
                  {userOffers.length === 0 ? (<p className="text-sm text-gray-500 text-center py-8">
                      {isRTL ? 'لا توجد عروض' : 'No offers'}
                    </p>) : (<div className="space-y-3 max-h-64 overflow-y-auto">
                      {userOffers.map((o) => (<div key={o.id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex justify-between items-start mb-1">
                            <h5 className="font-medium text-gray-900 text-sm">
                              {o.title}
                            </h5>
                            <span className="text-sm font-bold text-green-600">
                              {o.discount}
                            </span>
                          </div>
                          {(o.start_date || o.end_date) && (<p className="text-xs text-gray-500 mb-1">
                              {o.start_date} - {o.end_date}
                            </p>)}
                          {o.active !== undefined && (<span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${o.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {o.active
                                    ? isRTL
                                        ? 'نشط'
                                        : 'Active'
                                    : isRTL
                                        ? 'غير نشط'
                                        : 'Inactive'}
                            </span>)}
                        </div>))}
                    </div>)}
                </div>

                {/* Customers Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-[#4f1964]"/>
                    {isRTL ? 'العملاء' : 'Customers'}
                    <span className="ml-auto text-sm font-normal text-gray-500">
                      ({userCustomers.length})
                    </span>
                  </h4>
                  {userCustomers.length === 0 ? (<p className="text-sm text-gray-500 text-center py-8">
                      {isRTL ? 'لا يوجد عملاء' : 'No customers'}
                    </p>) : (<div className="space-y-3 max-h-64 overflow-y-auto">
                      {userCustomers.map((c) => (<div key={c.id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <h5 className="font-medium text-gray-900 text-sm mb-1">
                            {c.name}
                          </h5>
                          {c.phone && (<p className="text-xs text-gray-600" dir="ltr">
                              {c.phone}
                            </p>)}
                          {c.note && (<p className="text-xs text-gray-500 mt-1 line-clamp-2">
                              {c.note}
                            </p>)}
                        </div>))}
                    </div>)}
                </div>

                {/* Tickets Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#4f1964]"/>
                    {isRTL ? 'تذاكر الدعم' : 'Support Tickets'}
                    <span className="ml-auto text-sm font-normal text-gray-500">
                      ({userTickets.length})
                    </span>
                    <button onClick={() => setIsCreateTicketOpen(true)} className="ml-2 flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-[#4f1964] text-white hover:bg-[#3d1350] transition-colors">
                      <Plus className="w-3.5 h-3.5"/>
                      {isRTL ? 'تذكرة جديدة' : 'New Ticket'}
                    </button>
                  </h4>
                  {userTickets.length === 0 ? (<p className="text-sm text-gray-500 text-center py-8">
                      {isRTL ? 'لا توجد تذاكر' : 'No tickets'}
                    </p>) : (<div className="space-y-3 max-h-64 overflow-y-auto">
                      {userTickets.map((t) => (<div key={t.id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => {
                                setActiveSection('tickets');
                                handleTicketClick(t);
                            }}>
                          <div className="flex justify-between items-start mb-1">
                            <h5 className="font-medium text-gray-900 text-sm">
                              {t.subject}
                            </h5>
                            {getStatusBadge(t.status)}
                          </div>
                          <p className="text-xs text-gray-500">
                            {new Date(t.created_at!).toLocaleDateString()}
                          </p>
                        </div>))}
                    </div>)}
                </div>
              </div>

              {/* Activity Log Section */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mt-6">
                <h4 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-[#4f1964]"/>
                  {isRTL ? 'سجل النشاطات' : 'Activity Log'}
                  <span className="ml-auto text-sm font-normal text-gray-500">
                    ({userActivities.length})
                  </span>
                </h4>
                
                {userActivities.length === 0 ? (<div className="text-center py-12 text-gray-500">
                    <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3"/>
                    <p>{isRTL ? 'لا توجد نشاطات مسجلة' : 'No activities recorded'}</p>
                  </div>) : (<div className="relative border-l-2 border-gray-100 ml-3 md:ml-4 space-y-6 max-h-96 overflow-y-auto pr-4">
                    {userActivities.map((activity) => (<div key={activity.id} className="relative pl-6 md:pl-8">
                        {/* Timeline dot */}
                        <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ${activity.activity_type.includes('payment_success') ? 'bg-green-500' :
                                activity.activity_type.includes('payment_failed') ? 'bg-red-500' :
                                    activity.activity_type.includes('login') ? 'bg-blue-500' :
                                        activity.activity_type.includes('register') ? 'bg-purple-500' :
                                            activity.activity_type.includes('plan') ? 'bg-indigo-500' :
                                                'bg-gray-400'}`}/>
                        
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                          <div className="flex justify-between items-start mb-1">
                            <h5 className="font-medium text-gray-900 text-sm">
                              {isRTL ? activity.description_ar : activity.description}
                            </h5>
                            <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                              {new Date(activity.created_at!).toLocaleString()}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span className="capitalize bg-white px-2 py-1 rounded border border-gray-200">
                              {activity.activity_type.replace(/_/g, ' ')}
                            </span>
                            {activity.performed_by && (<span className="flex items-center gap-1">
                                <User className="w-3 h-3"/>
                                {activity.performed_by}
                              </span>)}
                          </div>
                          
                          {/* Show metadata if present and not empty */}
                          {activity.metadata && Object.keys(activity.metadata).length > 0 && (<div className="mt-3 bg-white p-2 rounded border border-gray-200 text-xs font-mono text-gray-600 overflow-x-auto">
                              {JSON.stringify(activity.metadata, null, 2)}
                            </div>)}
                        </div>
                      </div>))}
                  </div>)}
              </div>

              {/* Admin Create Ticket Modal */}
              <AnimatePresence>
                {isCreateTicketOpen && (<motion.div initial={{
                            opacity: 0,
                        }} animate={{
                            opacity: 1,
                        }} exit={{
                            opacity: 0,
                        }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !isCreatingTicket && setIsCreateTicketOpen(false)}>
                    <motion.div initial={{
                            scale: 0.95,
                            opacity: 0,
                        }} animate={{
                            scale: 1,
                            opacity: 1,
                        }} exit={{
                            scale: 0.95,
                            opacity: 0,
                        }} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
                      <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">
                            {isRTL
                            ? 'إنشاء تذكرة دعم'
                            : 'Create Support Ticket'}
                          </h3>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {isRTL ? 'لـ' : 'For'} {selectedUser?.name} (
                            {selectedUser?.email})
                          </p>
                        </div>
                        <button onClick={() => !isCreatingTicket && setIsCreateTicketOpen(false)} className="text-gray-400 hover:text-gray-600" disabled={isCreatingTicket}>
                          <X className="w-5 h-5"/>
                        </button>
                      </div>
                      <form onSubmit={handleAdminCreateTicket} className="p-6 space-y-5">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            {isRTL ? 'الموضوع' : 'Subject'}
                          </label>
                          <input type="text" value={createTicketSubject} onChange={(e) => setCreateTicketSubject(e.target.value)} placeholder={isRTL ? 'موضوع التذكرة...' : 'Ticket subject...'} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required disabled={isCreatingTicket}/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            {isRTL ? 'الأولوية' : 'Priority'}
                          </label>
                          <div className="grid grid-cols-3 gap-3">
                            {[
                            {
                                id: 'low' as const,
                                label: isRTL ? 'منخفضة' : 'Low',
                                color: 'green',
                            },
                            {
                                id: 'medium' as const,
                                label: isRTL ? 'متوسطة' : 'Medium',
                                color: 'yellow',
                            },
                            {
                                id: 'high' as const,
                                label: isRTL ? 'عالية' : 'High',
                                color: 'red',
                            },
                        ].map((p) => (<button key={p.id} type="button" onClick={() => setCreateTicketPriority(p.id)} className={`py-2 px-3 rounded-lg text-sm font-medium border transition-all ${createTicketPriority === p.id ? (p.color === 'green' ? 'bg-green-50 border-green-200 text-green-700' : p.color === 'yellow' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : 'bg-red-50 border-red-200 text-red-700') : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                                {p.label}
                              </button>))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            {isRTL ? 'الرسالة' : 'Message'}
                          </label>
                          <textarea value={createTicketMessage} onChange={(e) => setCreateTicketMessage(e.target.value)} placeholder={isRTL
                            ? 'اكتب رسالتك للمستخدم...'
                            : 'Write your message to the user...'} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none resize-none" rows={5} required disabled={isCreatingTicket}/>
                        </div>
                        <div className="pt-2 flex gap-3">
                          <Button type="button" variant="outline" onClick={() => setIsCreateTicketOpen(false)} className="flex-1" disabled={isCreatingTicket}>
                            {isRTL ? 'إلغاء' : 'Cancel'}
                          </Button>
                          <Button type="submit" className="flex-1 bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center justify-center gap-2" disabled={isCreatingTicket ||
                            !createTicketSubject.trim() ||
                            !createTicketMessage.trim()}>
                            {isCreatingTicket ? (<div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"/>) : (<>
                                <Send className="w-4 h-4"/>
                                {isRTL ? 'إنشاء التذكرة' : 'Create Ticket'}
                              </>)}
                          </Button>
                        </div>
                      </form>
                    </motion.div>
                  </motion.div>)}
              </AnimatePresence>
            </>)}
        </div>);
        }
        // Users Table View
        return (<div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">
            {isRTL ? 'المستخدمين' : 'Users'}
          </h2>
          <div className="relative w-full sm:w-64">
            <Search className="absolute top-2.5 left-3 w-5 h-5 text-gray-400"/>
            <input type="text" placeholder={isRTL ? 'بحث عن مستخدم...' : 'Search users...'} value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="w-full pl-10 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none"/>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-sm text-gray-500">
                  <th className="p-4 font-medium">
                    {isRTL ? 'الاسم' : 'Name'}
                  </th>
                  <th className="p-4 font-medium">
                    {isRTL ? 'البريد' : 'Email'}
                  </th>
                  <th className="p-4 font-medium">
                    {isRTL ? 'الهاتف' : 'Phone'}
                  </th>
                  <th className="p-4 font-medium">
                    {isRTL ? 'الخطة' : 'Plan'}
                  </th>
                  <th className="p-4 font-medium">
                    {isRTL ? 'الحالة' : 'Status'}
                  </th>
                  <th className="p-4 font-medium">
                    {isRTL ? 'تاريخ التسجيل' : 'Joined'}
                  </th>
                  <th className="p-4 font-medium">
                    {isRTL ? 'إجراءات' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((u) => (<tr key={u.id} className="hover:bg-gray-50">
                    <td className="p-4 text-sm font-medium text-gray-900">
                      {u.name}
                      {u.brand_name && (<span className="block text-xs text-gray-500 font-normal">
                          {u.brand_name}
                        </span>)}
                    </td>
                    <td className="p-4 text-sm text-gray-600">{u.email}</td>
                    <td className="p-4 text-sm text-gray-600" dir="ltr">
                      {u.phone || '-'}
                    </td>
                    <td className="p-4">{getPlanBadge(u.plan)}</td>
                    <td className="p-4">
                      {u.is_account_suspended ? (<span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Suspended
                        </span>) : (<span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>)}
                    </td>
                    <td className="p-4 text-sm text-gray-500">
                      {new Date(u.created_at!).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <Button onClick={() => loadUserDetails(u)} variant="outline" size="sm" className="text-[#4f1964] border-[#4f1964] hover:bg-[#4f1964] hover:text-white flex items-center gap-1.5">
                        <Eye className="w-4 h-4"/>
                        {isRTL ? 'إدارة' : 'Manage'}
                      </Button>
                    </td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        </div>
      </div>);
    };
    const renderTickets = () => {
        if (selectedTicket) {
            return (<div className="space-y-6 max-w-4xl mx-auto">
          <button onClick={() => setSelectedTicket(null)} className="text-gray-500 hover:text-[#4f1964] flex items-center gap-2 text-sm font-medium">
            <X className="w-4 h-4"/>
            {isRTL ? 'العودة للقائمة' : 'Back to list'}
          </button>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">
                    {selectedTicket.subject}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {isRTL ? 'بواسطة:' : 'By:'} {selectedTicket.user_email}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(selectedTicket.status)}
                  <select value={selectedTicket.status} onChange={(e) => handleUpdateTicketStatus(e.target.value as any)} className="text-sm border border-gray-300 rounded-lg p-1.5 focus:ring-2 focus:ring-[#4f1964] outline-none">
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-gray-700 whitespace-pre-wrap">
                {renderMessageContent(selectedTicket.message)}
              </div>
            </div>

            <div className="p-6 bg-gray-50 space-y-4">
              <h3 className="font-bold text-gray-900">
                {isRTL ? 'الردود' : 'Replies'}
              </h3>

              {ticketReplies.length === 0 ? (<p className="text-sm text-gray-500 text-center py-4">
                  {isRTL ? 'لا توجد ردود بعد' : 'No replies yet'}
                </p>) : (<div className="space-y-4">
                  {ticketReplies.map((reply) => (<div key={reply.id} className={`flex ${reply.from_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl p-4 ${reply.from_type === 'admin' ? 'bg-[#4f1964] text-white rounded-tr-none' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none'}`}>
                        <div className="flex items-center gap-2 mb-1 opacity-80 text-xs">
                          <span className="font-bold">{reply.from_name}</span>
                          <span>•</span>
                          <span>
                            {new Date(reply.created_at!).toLocaleString()}
                          </span>
                        </div>
                        {renderMessageContent(reply.message)}
                      </div>
                    </div>))}
                </div>)}

              {selectedTicket.status !== 'closed' &&
                    selectedTicket.status !== 'resolved' ? (<form onSubmit={handleReplyTicket} className="mt-6">
                  <div className="relative">
                    <textarea value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} placeholder={isRTL ? 'اكتب ردك هنا...' : 'Type your reply...'} className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none resize-none mb-3" rows={3}/>
                    <div className="absolute top-3 right-3">
                      <label className="cursor-pointer text-gray-400 hover:text-[#4f1964] transition-colors p-1 block">
                        <input type="file" accept="image/*" className="hidden" onChange={handleFileChange}/>
                        <Paperclip className="w-5 h-5"/>
                      </label>
                    </div>
                  </div>

                  {attachmentBase64 && (<div className="mb-4 relative inline-block">
                      <img src={attachmentBase64} alt="Preview" className="h-20 w-auto rounded border border-gray-200 object-cover"/>
                      <button type="button" onClick={() => setAttachmentBase64(null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600">
                        <X className="w-3 h-3"/>
                      </button>
                    </div>)}

                  <div className="flex justify-end">
                    <Button type="submit" className="bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center gap-2" disabled={isReplying ||
                        (!replyMessage.trim() && !attachmentBase64)}>
                      {isReplying ? (<div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/>) : (<Send className="w-4 h-4"/>)}
                      {isRTL ? 'إرسال الرد' : 'Send Reply'}
                    </Button>
                  </div>
                </form>) : (<div className="mt-6 p-4 bg-gray-100 rounded-lg text-center text-sm text-gray-500">
                  {isRTL
                        ? 'هذه التذكرة مغلقة ولا يمكن الرد عليها'
                        : 'This ticket is closed and cannot be replied to'}
                </div>)}
            </div>
          </div>
        </div>);
        }
        const filteredTickets = ticketStatusFilter === 'all'
            ? tickets
            : tickets.filter((t) => t.status === ticketStatusFilter);
        return (<div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {isRTL ? 'تذاكر الدعم' : 'Support Tickets'}
          </h2>
          <select value={ticketStatusFilter} onChange={(e) => setTicketStatusFilter(e.target.value)} className="border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-[#4f1964] outline-none">
            <option value="all">{isRTL ? 'الكل' : 'All'}</option>
            <option value="open">{isRTL ? 'مفتوحة' : 'Open'}</option>
            <option value="in_progress">
              {isRTL ? 'قيد المعالجة' : 'In Progress'}
            </option>
            <option value="resolved">{isRTL ? 'تم الحل' : 'Resolved'}</option>
            <option value="closed">{isRTL ? 'مغلقة' : 'Closed'}</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {filteredTickets.length === 0 ? (<div className="p-12 text-center text-gray-500">
              {isRTL ? 'لا توجد تذاكر' : 'No tickets found'}
            </div>) : (<div className="divide-y divide-gray-100">
              {filteredTickets.map((ticket) => (<div key={ticket.id} onClick={() => handleTicketClick(ticket)} className="p-5 hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="text-base font-semibold text-gray-900 truncate">
                          {ticket.subject}
                        </h4>
                        <span className="text-xs text-gray-500">
                          {ticket.user_email}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-1 mb-2">
                        {ticket.message.split('[ATTACHMENT:')[0].trim() ||
                        (isRTL ? 'مرفق' : 'Attachment')}
                      </p>
                      <div className="text-xs text-gray-400">
                        {new Date(ticket.created_at!).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {getStatusBadge(ticket.status)}
                    </div>
                  </div>
                </div>))}
            </div>)}
        </div>
      </div>);
    };
    const handleAddStaff = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!staffName || !staffEmail || !staffPassword) {
            showToast('error', isRTL ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
            return;
        }
        setIsAddingStaff(true);
        try {
            const hashed = await hashPassword(staffPassword);
            const newStaff = await dbAdminStaff.create({
                email: staffEmail,
                name: staffName,
                password: hashed,
                role: staffRole,
                permissions: staffPermissions,
                is_active: true,
            });
            if (newStaff) {
                setStaff([newStaff, ...staff]);
                setIsStaffModalOpen(false);
                setStaffName('');
                setStaffEmail('');
                setStaffPassword('');
                setStaffRole('support');
                setStaffPermissions([]);
                showToast('success', isRTL ? 'تم إضافة الموظف' : 'Staff member added');
            }
            else {
                showToast('error', isRTL ? 'فشل إضافة الموظف' : 'Failed to add staff');
            }
        }
        catch (error) {
            console.error('Add staff error:', error);
            showToast('error', isRTL ? 'حدث خطأ' : 'An error occurred');
        }
        finally {
            setIsAddingStaff(false);
        }
    };
    const handleDeleteStaff = async (id: string, email: string) => {
        if (email.toLowerCase() === SUPER_ADMIN.email.toLowerCase()) {
            showToast('error', isRTL ? 'لا يمكن حذف المدير الأساسي' : 'Cannot delete super admin');
            return;
        }
        if (!confirm(isRTL ? 'هل أنت متأكد من حذف هذا الموظف؟' : 'Delete this staff member?'))
            return;
        try {
            const success = await dbAdminStaff.delete(id);
            if (success) {
                setStaff(staff.filter((s) => s.id !== id));
                showToast('success', isRTL ? 'تم حذف الموظف' : 'Staff member deleted');
            }
        }
        catch (error) {
            console.error('Delete staff error:', error);
        }
    };
    const handleToggleStaffActive = async (id: string, currentActive: boolean) => {
        try {
            const success = await dbAdminStaff.update(id, {
                is_active: !currentActive,
            } as any);
            if (success) {
                setStaff(staff.map((s) => s.id === id
                    ? {
                        ...s,
                        is_active: !currentActive,
                    }
                    : s));
                showToast('success', isRTL ? 'تم تحديث الحالة' : 'Status updated');
            }
        }
        catch (error) {
            console.error('Toggle staff error:', error);
        }
    };
    const toggleStaffPermission = (perm: string) => {
        setStaffPermissions((prev) => prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]);
    };
    const ALL_PERMISSIONS = [
        {
            id: 'view_users',
            label: isRTL ? 'عرض المستخدمين' : 'View Users',
        },
        {
            id: 'manage_tickets',
            label: isRTL ? 'إدارة التذاكر' : 'Manage Tickets',
        },
        {
            id: 'manage_staff',
            label: isRTL ? 'إدارة الموظفين' : 'Manage Staff',
        },
        {
            id: 'send_notifications',
            label: isRTL ? 'إرسال إشعارات' : 'Send Notifications',
        },
    ];
    const renderStaff = () => {
        return (<div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {isRTL ? 'الموظفين' : 'Staff Management'}
          </h2>
          <Button onClick={() => setIsStaffModalOpen(true)} className="bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center gap-2">
            <Plus className="w-4 h-4"/>
            {isRTL ? 'إضافة موظف' : 'Add Staff'}
          </Button>
        </div>

        {/* Super Admin Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 flex items-center justify-between border-b border-gray-100 bg-purple-50/50">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#4f1964] flex items-center justify-center text-white font-bold">
                W
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">
                  {SUPER_ADMIN.name}
                </h4>
                <p className="text-sm text-gray-500">{SUPER_ADMIN.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Super Admin
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {isRTL ? 'نشط' : 'Active'}
              </span>
            </div>
          </div>

          {/* Staff List */}
          {staff.length === 0 ? (<div className="p-8 text-center text-gray-500">
              <Shield className="w-10 h-10 text-gray-300 mx-auto mb-3"/>
              <p>
                {isRTL
                    ? 'لا يوجد موظفين مضافين بعد'
                    : 'No staff members added yet'}
              </p>
            </div>) : (<div className="divide-y divide-gray-100">
              {staff.map((s) => (<div key={s.id} className="p-5 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold">
                      {s.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{s.name}</h4>
                      <p className="text-sm text-gray-500">{s.email}</p>
                      {s.permissions && s.permissions.length > 0 && (<div className="flex flex-wrap gap-1 mt-1">
                          {s.permissions.map((p) => (<span key={p} className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600">
                              {p}
                            </span>))}
                        </div>)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${s.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                      {s.role}
                    </span>
                    <button onClick={() => handleToggleStaffActive(s.id!, s.is_active)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${s.is_active ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}>
                      {s.is_active
                        ? isRTL
                            ? 'نشط'
                            : 'Active'
                        : isRTL
                            ? 'معطل'
                            : 'Inactive'}
                    </button>
                    <button onClick={() => handleDeleteStaff(s.id!, s.email)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </div>
                </div>))}
            </div>)}
        </div>

        {/* Add Staff Modal */}
        <AnimatePresence>
          {isStaffModalOpen && (<motion.div initial={{
                    opacity: 0,
                }} animate={{
                    opacity: 1,
                }} exit={{
                    opacity: 0,
                }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !isAddingStaff && setIsStaffModalOpen(false)}>
              <motion.div initial={{
                    scale: 0.95,
                    opacity: 0,
                }} animate={{
                    scale: 1,
                    opacity: 1,
                }} exit={{
                    scale: 0.95,
                    opacity: 0,
                }} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-900">
                    {isRTL ? 'إضافة موظف جديد' : 'Add New Staff'}
                  </h3>
                  <button onClick={() => !isAddingStaff && setIsStaffModalOpen(false)} className="text-gray-400 hover:text-gray-600" disabled={isAddingStaff}>
                    <X className="w-5 h-5"/>
                  </button>
                </div>
                <form onSubmit={handleAddStaff} className="p-6 space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {isRTL ? 'الاسم' : 'Name'}
                    </label>
                    <input type="text" value={staffName} onChange={(e) => setStaffName(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required disabled={isAddingStaff}/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {isRTL ? 'البريد الإلكتروني' : 'Email'}
                    </label>
                    <input type="email" value={staffEmail} onChange={(e) => setStaffEmail(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required dir="ltr" disabled={isAddingStaff}/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {isRTL ? 'كلمة المرور' : 'Password'}
                    </label>
                    <input type="password" value={staffPassword} onChange={(e) => setStaffPassword(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required dir="ltr" disabled={isAddingStaff}/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {isRTL ? 'الدور' : 'Role'}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button" onClick={() => setStaffRole('support')} className={`py-2.5 px-3 rounded-lg text-sm font-medium border transition-all ${staffRole === 'support' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                        {isRTL ? 'دعم فني' : 'Support'}
                      </button>
                      <button type="button" onClick={() => setStaffRole('admin')} className={`py-2.5 px-3 rounded-lg text-sm font-medium border transition-all ${staffRole === 'admin' ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                        {isRTL ? 'مدير' : 'Admin'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isRTL ? 'الصلاحيات' : 'Permissions'}
                    </label>
                    <div className="space-y-2">
                      {ALL_PERMISSIONS.map((perm) => (<label key={perm.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                          <input type="checkbox" checked={staffPermissions.includes(perm.id)} onChange={() => toggleStaffPermission(perm.id)} className="w-4 h-4 text-[#4f1964] rounded border-gray-300 focus:ring-[#4f1964]"/>
                          <span className="text-sm text-gray-700">
                            {perm.label}
                          </span>
                        </label>))}
                    </div>
                  </div>
                  <div className="pt-2 flex gap-3">
                    <Button type="button" variant="outline" onClick={() => setIsStaffModalOpen(false)} className="flex-1" disabled={isAddingStaff}>
                      {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button type="submit" className="flex-1 bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center justify-center gap-2" disabled={isAddingStaff}>
                      {isAddingStaff ? (<div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"/>) : (<>
                          <Plus className="w-4 h-4"/>
                          {isRTL ? 'إضافة' : 'Add'}
                        </>)}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>)}
        </AnimatePresence>
      </div>);
    };
    const renderNotifications = () => {
        return (<div className="space-y-6 max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900">
          {isRTL ? 'إرسال إشعار' : 'Send Notification'}
        </h2>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-6">
          <form onSubmit={handleSendNotification} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {isRTL ? 'المستلم (البريد الإلكتروني)' : 'Recipient (Email)'}
              </label>
              <select value={notifEmail} onChange={(e) => setNotifEmail(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none" required>
                <option value="">
                  {isRTL ? 'اختر مستخدم...' : 'Select user...'}
                </option>
                <option value="all" className="font-bold text-[#4f1964]">
                  {isRTL ? 'جميع المستخدمين (All Users)' : 'All Users'}
                </option>
                {users.map((u) => (<option key={u.id} value={u.email}>
                    {u.name} ({u.email})
                  </option>))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {isRTL ? 'نوع الإشعار' : 'Notification Type'}
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                {
                    id: 'info',
                    label: isRTL ? 'معلومة' : 'Info',
                    color: 'blue',
                },
                {
                    id: 'warning',
                    label: isRTL ? 'تنبيه' : 'Warning',
                    color: 'yellow',
                },
                {
                    id: 'success',
                    label: isRTL ? 'نجاح' : 'Success',
                    color: 'green',
                },
            ].map((t) => (<button key={t.id} type="button" onClick={() => setNotifType(t.id as any)} className={`py-2 px-3 rounded-lg text-sm font-medium border transition-all ${notifType === t.id ? (t.color === 'yellow' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : t.color === 'green' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-blue-50 border-blue-200 text-blue-700') : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                    {t.label}
                  </button>))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 border-b pb-2">
                  English
                </h4>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Title
                  </label>
                  <input type="text" value={notifTitleEn} onChange={(e) => setNotifTitleEn(e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none text-sm" required dir="ltr"/>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Message
                  </label>
                  <textarea value={notifMessageEn} onChange={(e) => setNotifMessageEn(e.target.value)} rows={4} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none text-sm resize-none" required dir="ltr"/>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 border-b pb-2">
                  العربية
                </h4>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    العنوان
                  </label>
                  <input type="text" value={notifTitleAr} onChange={(e) => setNotifTitleAr(e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none text-sm" required dir="rtl"/>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    الرسالة
                  </label>
                  <textarea value={notifMessageAr} onChange={(e) => setNotifMessageAr(e.target.value)} rows={4} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none text-sm resize-none" required dir="rtl"/>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100 flex justify-end">
              <Button type="submit" className="bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center gap-2" disabled={isSendingNotif}>
                {isSendingNotif ? (<div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"/>) : (<Send className="w-4 h-4"/>)}
                {isRTL ? 'إرسال الإشعار' : 'Send Notification'}
              </Button>
            </div>
          </form>
        </div>
      </div>);
    };
    return (<div className="min-h-screen bg-gray-50 flex" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (<motion.div initial={{
                opacity: 0,
            }} animate={{
                opacity: 1,
            }} exit={{
                opacity: 0,
            }} onClick={() => setMobileMenuOpen(false)} className="fixed inset-0 bg-black/50 z-40 md:hidden"/>)}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside className={`fixed md:static inset-y-0 ${isRTL ? 'right-0' : 'left-0'} z-50 w-64 bg-[#4f1964] text-white transform transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'} md:translate-x-0 flex flex-col`}>
        <div className="p-5 border-b border-purple-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center text-white">
              <Shield className="w-5 h-5"/>
            </div>
            <span className="font-bold text-lg">Admin Panel</span>
          </div>
          <button onClick={() => setMobileMenuOpen(false)} className="md:hidden text-white">
            <X className="w-6 h-6"/>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {sidebarItems.map((item) => (<button key={item.id} onClick={() => {
                setActiveSection(item.id as AdminSection);
                setMobileMenuOpen(false);
            }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeSection === item.id ? 'bg-white/15 text-white font-medium' : 'text-purple-200 hover:bg-white/5 hover:text-white'}`}>
              <item.icon className="w-5 h-5"/>
              <span className="flex-1 text-start">{item.label}</span>
            </button>))}
        </nav>

        <div className="p-4 border-t border-purple-800">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-sm">
              {adminUser.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {adminUser.name}
              </p>
              <p className="text-xs text-purple-300 truncate capitalize">
                {adminUser.role.replace('_', ' ')}
              </p>
            </div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-red-300 hover:bg-red-500/10 hover:text-red-200 transition-colors">
            <LogOut className="w-5 h-5"/>
            <span className="text-sm font-medium">
              {isRTL ? 'تسجيل الخروج' : 'Logout'}
            </span>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <button onClick={() => setMobileMenuOpen(true)} className="md:hidden text-gray-500">
              <Menu className="w-6 h-6"/>
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              {sidebarItems.find((i) => i.id === activeSection)?.label}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {isLoadingData && (<div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="w-4 h-4 border-2 border-[#4f1964]/30 border-t-[#4f1964] rounded-full animate-spin"/>
                {isRTL ? 'تحديث...' : 'Syncing...'}
              </div>)}
            <Button variant="outline" size="sm" onClick={loadDashboardData} disabled={isLoadingData}>
              {isRTL ? 'تحديث البيانات' : 'Refresh'}
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <AnimatePresence mode="wait">
            <motion.div key={activeSection} initial={{
            opacity: 0,
            y: 10,
        }} animate={{
            opacity: 1,
            y: 0,
        }} exit={{
            opacity: 0,
            y: -10,
        }} transition={{
            duration: 0.2,
        }}>
              {activeSection === 'overview' && renderOverview()}
              {activeSection === 'users' && renderUsers()}
              {activeSection === 'tickets' && renderTickets()}
              {activeSection === 'staff' && renderStaff()}
              {activeSection === 'notifications' && renderNotifications()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>);
}
